import React from 'react';
import { PieChart, CheckCircle2, TrendingUp, Award, Globe, Building, Activity, Leaf as LeafIcon, TreePine, Car, Home, Plane, Zap, Calendar } from 'lucide-react';

export default function PortfolioDashboard() {
    const mockPortfolioData = {
        user_id: 'user_12345',
        total_credits_owned: 150,
        total_credits_retired: 75,
        total_co2_offset_tonnes: 75,
        total_investment_value: 187500,
        portfolio_growth: 12.5,
        sdg_contributions: [
            { sdg_number: 13, sdg_name: 'Climate Action', percentage: 100, icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-13.avif' },
            { sdg_number: 15, sdg_name: 'Life on Land', percentage: 67, icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-15.avif' },
            { sdg_number: 7, sdg_name: 'Clean Energy', percentage: 50, icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-07.avif' },
            { sdg_number: 14, sdg_name: 'Life Below Water', percentage: 25, icon: '/assets/E-SDG-Icons-WEB/E SDG Icons WEB/E-WEB-Goal-14.avif' }
        ],
        projects_supported: [
            {
                project_id: 401,
                project_name: 'Amazon Rainforest Conservation',
                credits_owned: 50,
                credits_retired: 30,
                investment_amount: 75000,
                image: 'https://images.unsplash.com/photo-1511497584788-876760111969?w=100&h=100&fit=crop'
            },
            {
                project_id: 402,
                project_name: 'Solar Farm Carbon Offset',
                credits_owned: 40,
                credits_retired: 25,
                investment_amount: 60000,
                image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=100&h=100&fit=crop'
            },
            {
                project_id: 403,
                project_name: 'Wind Farm Brazil',
                credits_owned: 35,
                credits_retired: 20,
                investment_amount: 52500,
                image: 'https://images.unsplash.com/photo-1466611653911-95281773ad90?w=100&h=100&fit=crop'
            }
        ],
        retirement_history: [
            {
                certificate_id: 'CERT-2026-001234',
                date: '2026-03-15T10:30:00Z',
                project_name: 'Amazon Rainforest Conservation',
                amount_tonnes: 25,
                beneficiary_name: 'John Doe',
                status: 'Verified'
            },
            {
                certificate_id: 'CERT-2026-001189',
                date: '2026-03-10T14:20:00Z',
                project_name: 'Solar Farm Carbon Offset',
                amount_tonnes: 30,
                beneficiary_name: 'John Doe',
                status: 'Verified'
            },
            {
                certificate_id: 'CERT-2026-000987',
                date: '2026-02-28T09:15:00Z',
                project_name: 'Amazon Rainforest Conservation',
                amount_tonnes: 20,
                beneficiary_name: 'Jane Smith',
                status: 'Verified'
            }
        ],
        impact_timeline: [
            { month: 'Oct 2025', credits_retired: 10, co2_offset: 10 },
            { month: 'Nov 2025', credits_retired: 15, co2_offset: 15 },
            { month: 'Dec 2025', credits_retired: 12, co2_offset: 12 },
            { month: 'Jan 2026', credits_retired: 18, co2_offset: 18 },
            { month: 'Feb 2026', credits_retired: 20, co2_offset: 20 },
            { month: 'Mar 2026', credits_retired: 25, co2_offset: 25 }
        ],
        impact_equivalents: {
            trees_planted: 3750,
            car_km_offset: 300000,
            homes_powered_months: 90,
            flights_offset: 150,
            smartphones_charged: 9000000
        }
    };

    const data = mockPortfolioData;

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-600 rounded-xl">
                    <PieChart className="text-white" size={24} />
                </div>
                <div>
                    <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Portfolio Impact Dashboard</h3>
                    <p className="text-xs text-gray-600 font-medium">Your total environmental contribution</p>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-100">
                    <div className="flex items-center gap-2 mb-3">
                        <LeafIcon className="text-emerald-600" size={20} />
                        <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-widest">CO₂ Offset</span>
                    </div>
                    <p className="text-2xl font-semibold text-gray-900">{data.total_co2_offset_tonnes}</p>
                    <p className="text-[10px] font-medium text-gray-500 uppercase mt-1">Tonnes</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="text-blue-600" size={20} />
                        <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-widest">Retired</span>
                    </div>
                    <p className="text-2xl font-semibold text-gray-900">{data.total_credits_retired}</p>
                    <p className="text-[10px] font-medium text-gray-500 uppercase mt-1">Credits</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="text-purple-600" size={20} />
                        <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-widest">Investment</span>
                    </div>
                    <p className="text-2xl font-semibold text-gray-900">AED {(data.total_investment_value / 1000).toFixed(0)}K</p>
                    <p className="text-[10px] font-medium text-gray-500 uppercase mt-1">Total Value</p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
                    <div className="flex items-center gap-2 mb-3">
                        <Award className="text-orange-600" size={20} />
                        <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-widest">Growth</span>
                    </div>
                    <p className="text-2xl font-semibold text-emerald-600">+{data.portfolio_growth}%</p>
                    <p className="text-[10px] font-medium text-gray-500 uppercase mt-1">Portfolio ROI</p>
                </div>
            </div>

            {/* SDG Contributions */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <Globe className="text-blue-600" size={20} />
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">SDG Contributions</h4>
                </div>
                <div className="space-y-4">
                    {data.sdg_contributions.map((sdg, index) => (
                        <div key={index}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center p-1">
                                        <img src={sdg.icon} alt={sdg.sdg_name} className="w-full h-full object-contain" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-900">SDG {sdg.sdg_number}: {sdg.sdg_name}</p>
                                        <p className="text-[9px] text-gray-500">Contribution Level</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-semibold text-gray-900">{sdg.percentage}%</p>
                                </div>
                            </div>
                            <div className="relative h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-green-400 rounded-full"
                                    style={{ width: `${sdg.percentage}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Projects Supported */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <Building className="text-purple-600" size={20} />
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Projects Supported</h4>
                </div>
                <div className="space-y-4">
                    {data.projects_supported.map((project, index) => (
                        <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <img
                                src={project.image}
                                alt={project.project_name}
                                className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-900 mb-1">{project.project_name}</p>
                                <div className="flex items-center gap-4 text-[10px] text-gray-500">
                                    <span>Owned: <span className="font-medium text-gray-700">{project.credits_owned} tCO₂e</span></span>
                                    <span>Retired: <span className="font-medium text-emerald-600">{project.credits_retired} tCO₂e</span></span>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm font-semibold text-gray-900">AED {(project.investment_amount / 1000).toFixed(0)}K</p>
                                <p className="text-[9px] font-medium text-gray-500 uppercase">Investment</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Impact Timeline Chart */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <Activity className="text-blue-600" size={20} />
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Impact Timeline</h4>
                </div>
                <div className="flex items-end gap-3 h-40">
                    {data.impact_timeline.map((item, index) => {
                        const maxCredits = Math.max(...data.impact_timeline.map(i => i.credits_retired));
                        const height = (item.credits_retired / maxCredits) * 100;
                        return (
                            <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                <div
                                    className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-md transition-all hover:from-emerald-700 hover:to-emerald-500"
                                    style={{ height: `${height}%` }}
                                />
                                <div className="text-center">
                                    <p className="text-[9px] font-medium text-gray-500">{item.month.split(' ')[0]}</p>
                                    <p className="text-[10px] font-semibold text-emerald-600">{item.credits_retired}t</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Retirement History */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <Award className="text-purple-600" size={20} />
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Retirement Certificates</h4>
                </div>
                <div className="space-y-3">
                    {data.retirement_history.map((retirement, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-emerald-100 rounded-xl">
                                    <Award className="text-emerald-600" size={20} />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-gray-900">{retirement.project_name}</p>
                                    <div className="flex items-center gap-3 text-[9px] text-gray-500">
                                        <span className="flex items-center gap-1">
                                            <Calendar size={12} />
                                            {new Date(retirement.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </span>
                                        <span>•</span>
                                        <span>{retirement.certificate_id}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-lg font-semibold text-emerald-600">{retirement.amount_tonnes} <span className="text-xs font-medium">tCO₂e</span></p>
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[9px] font-semibold uppercase">
                                    <CheckCircle2 size={10} />
                                    {retirement.status}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Impact Equivalents */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-center gap-3 mb-6">
                    <LeafIcon className="text-blue-600" size={20} />
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Your Impact in Real Terms</h4>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-blue-100 text-center">
                        <TreePine className="text-green-600 mx-auto mb-2" size={24} />
                        <p className="text-xl font-semibold text-gray-900">{(data.impact_equivalents.trees_planted / 1000).toFixed(1)}K</p>
                        <p className="text-[9px] font-medium text-gray-500 uppercase">Trees Planted</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-100 text-center">
                        <Car className="text-orange-600 mx-auto mb-2" size={24} />
                        <p className="text-xl font-semibold text-gray-900">{(data.impact_equivalents.car_km_offset / 1000).toFixed(0)}K</p>
                        <p className="text-[9px] font-medium text-gray-500 uppercase">Km Offset</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-100 text-center">
                        <Home className="text-yellow-600 mx-auto mb-2" size={24} />
                        <p className="text-xl font-semibold text-gray-900">{data.impact_equivalents.homes_powered_months}</p>
                        <p className="text-[9px] font-medium text-gray-500 uppercase">Months Powered</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-100 text-center">
                        <Plane className="text-sky-600 mx-auto mb-2" size={24} />
                        <p className="text-xl font-semibold text-gray-900">{data.impact_equivalents.flights_offset}</p>
                        <p className="text-[9px] font-medium text-gray-500 uppercase">Flights Offset</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-100 text-center">
                        <Zap className="text-purple-600 mx-auto mb-2" size={24} />
                        <p className="text-xl font-semibold text-gray-900">{(data.impact_equivalents.smartphones_charged / 1000000).toFixed(1)}M</p>
                        <p className="text-[9px] font-medium text-gray-500 uppercase">Phones Charged</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
